// © 2026 AttainIP Engineering
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileSearch, MessageSquare, MessagesSquare, CreditCard, Activity, LogOut, Building2, KeyRound } from 'lucide-react';
import { clearTokens, getCurrentUser, isPlatformAdmin } from '@/lib/auth';
import { useAdminPrefs } from '@/lib/adminPrefs';

// Platform-wide pages — require platform_admin; org_admin never sees them.
const platformNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/analyses', label: 'Analyses', icon: FileSearch },
  { to: '/public-chats', label: 'Public Chats', icon: MessagesSquare },
  { to: '/payments', label: 'Payments', icon: CreditCard },
  { to: '/feedback', label: 'Feedback', icon: MessageSquare },
  { to: '/events', label: 'Audit Log', icon: Activity },
  { to: '/case-ownership', label: 'Case Ownership', icon: KeyRound },
];

export function Layout() {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const platformAdmin = isPlatformAdmin(user);
  const { hideInternal, setHideInternal } = useAdminPrefs();

  // platform_admin sees every platform page plus the Enterprises list;
  // org_admin (without platform_admin) sees only its own firm detail
  // (RFC-0212 A2).
  const navItems = platformAdmin
    ? [...platformNavItems, { to: '/enterprises', label: 'Enterprises', icon: Building2 }]
    : [{ to: `/enterprises/${user?.organizationId ?? ''}`, label: 'My Firm', icon: Building2 }];

  const handleLogout = () => {
    clearTokens();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-56 bg-gray-900 text-white flex flex-col">
        <div className="p-4 border-b border-gray-700">
          <h1 className="text-lg font-semibold">Attain IP SelfService Admin</h1>
          {user && <p className="text-xs text-gray-400 mt-1">{user.email}</p>}
        </div>
        <nav className="flex-1 p-2">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded text-sm ${
                  isActive ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-2 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded text-sm text-gray-300 hover:bg-gray-800 hover:text-white w-full"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Portal-wide header bar — global preferences live here. */}
        <div className="flex items-center justify-end gap-4 px-6 py-2 border-b bg-white text-xs text-gray-600">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={hideInternal}
              onChange={(e) => setHideInternal(e.target.checked)}
              className="rounded"
            />
            Hide internal
          </label>
        </div>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
